import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import AnimateIn from '../components/AnimateIn';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const staggerContainer = {
    animate: {
      transition: { staggerChildren: 0.15 },
    },
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden" data-testid="hero-section">
        {/* Video background on the right, fading to black on the left */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 h-full" style={{ width: '70%' }}>
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster="/hero-poster.jpg"
              className="w-full h-full object-cover"
              data-testid="hero-video"
            >
              <source src="/hero-video.mp4" type="video/mp4" />
            </video>
          </div>
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, rgba(5,5,5,1) 0%, rgba(5,5,5,1) 30%, rgba(5,5,5,0.85) 48%, rgba(5,5,5,0.35) 65%, rgba(5,5,5,0.05) 80%)',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, rgba(5,5,5,0.25) 0%, rgba(5,5,5,0) 30%, rgba(5,5,5,0) 70%, rgba(5,5,5,1) 100%)',
            }}
          />
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto w-full px-6 lg:px-8 pt-24 md:pt-0">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <motion.div
              className="hero-accent-line"
              variants={fadeInUp}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            />

            <motion.p
              className="text-sm font-medium tracking-[0.15em] uppercase mb-6"
              style={{ color: '#e2c179' }}
              variants={fadeInUp}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="hero-tagline"
            >
              {t('heroTagline')}
            </motion.p>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-[3.75rem] font-bold leading-[1.08] tracking-[-0.02em] text-white max-w-3xl mb-8"
              style={{ fontFamily: "'Syne', sans-serif" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="hero-title"
            >
              {t('heroTitle')}
            </motion.h1>

            <motion.p
              className="text-base md:text-lg leading-relaxed max-w-2xl mb-12 text-gray-400"
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              data-testid="hero-subtitle"
            >
              {t('heroSubtitle')}
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4"
              variants={fadeInUp}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <button
                className="btn-primary"
                onClick={() => navigate('/services')}
                data-testid="hero-cta-primary"
              >
                {t('heroCtaPrimary')}
                <ArrowRight size={16} />
              </button>
              <button
                className="btn-outline"
                onClick={() => navigate('/ecosystem')}
                data-testid="hero-cta-secondary"
              >
                {t('heroCtaSecondary')}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 md:py-28 px-6 lg:px-8 border-t border-white/[0.07]" data-testid="stats-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-8">
            <AnimateIn delay={0} direction="up">
              <div className="text-center md:text-left">
                <p className="stat-number" data-testid="stat-1-number">{t('stat1Number')}</p>
                <p className="stat-label">{t('stat1Label')}</p>
              </div>
            </AnimateIn>
            <AnimateIn delay={0.15} direction="up">
              <div className="text-center md:text-left md:border-l md:border-white/10 md:pl-8">
                <p className="stat-number" data-testid="stat-2-number">{t('stat2Number')}</p>
                <p className="stat-label">{t('stat2Label')}</p>
              </div>
            </AnimateIn>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 md:py-28 px-6 lg:px-8" style={{ background: '#0a0a0a' }} data-testid="services-preview">
        <div className="max-w-[1200px] mx-auto">
          <AnimateIn className="text-center mb-16">
            <p className="accent-tag justify-center">{t('servicesTag')}</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
              {t('servicesTitle')}
            </h2>
          </AnimateIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { img: '/wallpapers/wwd-web.jpg', color: '#60A5FA', title: t('service1Title'), desc: t('service1Desc') },
              { img: '/wallpapers/wwd-platforms.jpg', color: '#e2c179', title: t('service2Title'), desc: t('service2Desc') },
              { img: '/wallpapers/wwd-growth.jpg', color: '#34D399', title: t('service3Title'), desc: t('service3Desc') },
            ].map((service, i) => (
              <AnimateIn key={i} delay={i * 0.12} direction="up">
                <div
                  className="relative overflow-hidden rounded-2xl border border-white/10 h-[440px] group cursor-pointer transition-transform duration-500 hover:-translate-y-1.5"
                  onClick={() => navigate('/services')}
                  data-testid={`service-card-${i}`}
                >
                  <img
                    src={service.img}
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-60 transition-all duration-700 group-hover:opacity-90 group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(180deg, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.1) 40%, rgba(5,5,5,0.94) 82%)' }}
                  />
                  <div
                    className="absolute top-0 left-0 right-0 h-[3px] opacity-80"
                    style={{ background: `linear-gradient(90deg, ${service.color}, transparent)` }}
                  />
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <p
                      className="text-[11px] font-bold tracking-[0.3em] mb-3"
                      style={{ color: service.color, fontFamily: "'Syne', sans-serif" }}
                    >
                      0{i + 1}
                    </p>
                    <h3
                      className="text-xl font-bold text-white mb-3"
                      style={{ fontFamily: "'Syne', sans-serif" }}
                    >
                      {service.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-400 mb-4">{service.desc}</p>
                    <span
                      className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"
                      style={{ color: service.color }}
                    >
                      {t('viewPricing')}
                      <ArrowRight size={13} />
                    </span>
                  </div>
                </div>
              </AnimateIn>
            ))}
          </div>

          <AnimateIn className="text-center mt-12" delay={0.4}>
            <button
              className="btn-accent"
              onClick={() => navigate('/services')}
              data-testid="view-all-services-btn"
            >
              {t('viewPricing')}
              <ArrowRight size={16} />
            </button>
          </AnimateIn>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-20 md:py-28 px-6 lg:px-8" data-testid="why-us-section">
        <div className="max-w-[1200px] mx-auto">
          <AnimateIn className="text-center mb-6">
            <p className="accent-tag justify-center">{t('whyUsTag')}</p>
            <h2
              className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white mb-4"
              style={{ fontFamily: "'Syne', sans-serif" }}
              data-testid="why-us-title"
            >
              {t('whyUsTitle')}
            </h2>
            <p className="text-base max-w-2xl mx-auto text-gray-400">
              {t('whyUsSubtitle')}
            </p>
          </AnimateIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
            {[
              { title: t('whyUs1Title'), desc: t('whyUs1Desc') },
              { title: t('whyUs2Title'), desc: t('whyUs2Desc') },
              { title: t('whyUs3Title'), desc: t('whyUs3Desc') },
              { title: t('whyUs4Title'), desc: t('whyUs4Desc') },
            ].map((item, i) => (
              <AnimateIn
                key={i}
                delay={i * 0.1}
                direction={i % 2 === 0 ? 'left' : 'right'}
              >
                <div
                  className="p-8 rounded-2xl border border-white/10 bg-white/[0.03] transition-all hover:border-white/20 hover:-translate-y-0.5 h-full"
                  data-testid={`why-us-card-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: 'linear-gradient(135deg, var(--accent), #e2c179)' }}
                    >
                      <Check size={16} className="text-black" />
                    </div>
                    <div>
                      <h3
                        className="text-base font-semibold text-white mb-2"
                        style={{ fontFamily: "'Syne', sans-serif" }}
                      >
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-400">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 lg:px-8" data-testid="cta-section">
        <div className="max-w-[1200px] mx-auto">
          <AnimateIn direction="up" duration={0.8}>
            <div
              className="relative overflow-hidden rounded-2xl border border-white/10 p-12 md:p-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8"
              style={{ background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 50%, #2a2520 100%)' }}
            >
              <div
                className="absolute top-0 right-0 w-64 h-64 rounded-full blur-[100px] opacity-20"
                style={{ background: '#afa283' }}
              />
              <div className="relative z-10">
                <h2
                  className="text-2xl md:text-3xl font-bold tracking-[-0.02em] text-white mb-3"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                >
                  {t('contactTitle')}
                </h2>
                <p className="text-gray-400 text-base max-w-lg">
                  {t('contactSubtitle')}
                </p>
              </div>
              <button
                className="btn-accent shrink-0 relative z-10"
                onClick={() => navigate('/contact')}
                data-testid="cta-contact-btn"
              >
                {t('contact')}
                <ArrowRight size={16} />
              </button>
            </div>
          </AnimateIn>
        </div>
      </section>
    </div>
  );
};

export default Home;
